import { useEffect, useRef, useState } from 'react';
import { isNative } from './native';
import { startNativeWatch, stopNativeWatch } from './native-gps';

// Haversine distance in metres between two {lat,lng}
function metersBetween(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Absolute upper bound on human running speed (elite marathoners top out at
// ~6.5 m/s over 2h; sprint bursts of world-class 100m runners are ~12 m/s;
// GPS jumps regularly clock 30+ m/s so 15 m/s is a safe cutoff).
const MAX_HUMAN_SPEED_MPS = 15;
// Ignore fixes that are wildly imprecise once we have a lock.
const MAX_ACCEPTABLE_ACC_M = 65;
// Time-based dedupe: reject fixes closer than this to the previous one.
const MIN_FIX_INTERVAL_MS = 350;

/**
 * Real GPS tracker using the browser Geolocation API.
 *
 * Improvements vs. naive watchPosition:
 *   1. Two prewarm reads (cached low-accuracy + fresh high-accuracy) for a
 *      fast first fix.
 *   2. Adaptive noise floor: reject motion smaller than the position
 *      uncertainty — prevents "stationary drift" from inflating distance.
 *   3. GPS-jump rejection: reject a point whose implied speed exceeds
 *      15 m/s AND whose delta exceeds 20 m — kills teleport-style errors
 *      without losing legitimate sprint bursts.
 *   4. Time dedupe: reject fixes closer than 350 ms apart.
 *   5. Poor-accuracy points still keep the map moving but are gated by
 *      "did I actually move more than the error bar?".
 *
 * Returns:
 *   { supported, permission, error, accuracy, path, distanceMeters, reset }
 */
export function useGpsTracker({ active = true, enabled = true } = {}) {
  const [supported] = useState(() => typeof navigator !== 'undefined' && 'geolocation' in navigator);
  const [error, setError] = useState(null);
  const [permission, setPermission] = useState('prompt');
  const [accuracy, setAccuracy] = useState(null);
  const [path, setPath] = useState([]);
  const [distanceMeters, setDistanceMeters] = useState(0);

  const watchIdRef = useRef(null);
  const lastPointRef = useRef(null);
  const activeRef = useRef(active);
  activeRef.current = active;

  // Prewarm — instant cached fix, then fresh high-accuracy fix.
  useEffect(() => {
    if (!enabled || !supported) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => { setAccuracy(pos.coords.accuracy); setError(null); },
      () => {},
      { enableHighAccuracy: false, maximumAge: 300000, timeout: 4000 }
    );
    navigator.geolocation.getCurrentPosition(
      (pos) => { setAccuracy(pos.coords.accuracy); setError(null); },
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 12000 }
    );
  }, [enabled, supported]);

  useEffect(() => {
    if (!enabled || !supported) return;

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((p) => {
          setPermission(p.state);
          p.onchange = () => setPermission(p.state);
        })
        .catch(() => {});
    }

    // Shared fix-processing pipeline — runs for both web Geolocation
    // callbacks AND native BackgroundGeolocation emissions so the
    // noise-floor / jump-rejection logic is identical on every platform.
    const handleFix = (pos) => {
      setError(null);
      const acc = pos.coords.accuracy || 999;
      setAccuracy(acc);
      if (!activeRef.current) return;

      const point = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        t: pos.timestamp || Date.now(),
        acc,
      };

      if (!lastPointRef.current) {
        lastPointRef.current = point;
        setPath((p) => [...p, point]);
        return;
      }

      const dt = point.t - lastPointRef.current.t;
      if (dt < MIN_FIX_INTERVAL_MS) return;

      const d = metersBetween(lastPointRef.current, point);
      const speedMps = dt > 0 ? d / (dt / 1000) : 0;

      if (speedMps > MAX_HUMAN_SPEED_MPS && d > 20) return;

      const noiseFloor = Math.max(
        1.5,
        ((lastPointRef.current.acc || 0) + acc) * 0.25
      );
      if (d < noiseFloor) return;

      if (acc > MAX_ACCEPTABLE_ACC_M && d < acc) return;

      setDistanceMeters((m) => m + d);
      lastPointRef.current = point;
      setPath((p) => [...p, point]);
    };

    const handleError = (err) => setError((err && err.message) || 'GPS error');

    // NATIVE PATH — Capacitor BackgroundGeolocation plugin, which keeps a
    // sticky foreground service alive on Android and uses the proper
    // "always" location mode on iOS. Survives screen lock + app
    // backgrounding (the whole reason RIVL wraps as native).
    let nativeWatchId = null;
    let cancelled = false;
    if (isNative()) {
      startNativeWatch(handleFix, handleError).then((id) => {
        if (cancelled) {
          if (id) stopNativeWatch(id);
          return;
        }
        nativeWatchId = id;
      });
      return () => {
        cancelled = true;
        if (nativeWatchId) stopNativeWatch(nativeWatchId);
      };
    }

    // WEB PATH — standard watchPosition.
    const id = navigator.geolocation.watchPosition(
      handleFix,
      handleError,
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
    watchIdRef.current = id;

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, [enabled, supported]);

  function reset() {
    setPath([]);
    setDistanceMeters(0);
    lastPointRef.current = null;
  }

  return { supported, permission, error, accuracy, path, distanceMeters, reset };
}
